/**
 * Project Enhancement Service
 * Enhances project data with localStorage thumbnails for instant loading
 */

import localThumbnailService from './localThumbnailService';

/** True if the API already gave a thumbnail URL we should trust over localStorage. */
function hasUsableServerThumbnail(thumbnailUrl) {
    if (thumbnailUrl == null) return false;
    const s = String(thumbnailUrl).trim();
    return s !== '' && s.toLowerCase() !== 'null';
}

class ProjectEnhancementService {
    constructor() {
        this.thumbnailService = localThumbnailService;
    }

    /**
     * Enhance projects with thumbnails from localStorage
     * This provides instant thumbnail loading without backend calls
     */
    enhanceProjectsWithThumbnails(projects) {
        if (!projects || !Array.isArray(projects)) {
            console.warn('⚠️ Invalid projects data provided');
            return projects;
        }

        // Check if cache is ready
        if (!this.thumbnailService.isCacheReady()) {
            console.log('📦 Thumbnail cache not ready, projects will use fallback thumbnails');
            return projects;
        }

        // Get project IDs
        const projectIds = projects.map(project => project.Id || project.id).filter(Boolean);
        
        if (projectIds.length === 0) {
            console.log('📦 No valid project IDs found');
            return projects;
        }

        // Get thumbnails from localStorage
        const thumbnails = this.thumbnailService.getThumbnailsForProjects(projectIds);
        
        console.log(`📦 Enhancing ${projects.length} projects with ${Object.keys(thumbnails).length} thumbnails from localStorage`);

        // Enhance projects with thumbnails (localStorage must NOT override API)
        const enhancedProjects = projects.map(project => {
            const projectId = project.Id || project.id;
            // JSON/localStorage keys are strings; normalize lookup
            const cachedUrl =
                thumbnails[String(projectId)] ?? thumbnails[projectId];

            // Server /user-projects already returns verified ThumbnailURL or null — trust it.
            if (hasUsableServerThumbnail(project.ThumbnailURL)) {
                return project;
            }

            if (cachedUrl) {
                return {
                    ...project,
                    ThumbnailURL: cachedUrl,
                    ThumbnailSource: 'localStorage',
                };
            }

            return project;
        });

        return enhancedProjects;
    }

    /**
     * Get cache statistics for debugging
     */
    getCacheStats() {
        return this.thumbnailService.getCacheStats();
    }

    /**
     * Force refresh cache
     */
    forceRefreshCache() {
        this.thumbnailService.forceRefreshCache();
    }

    /**
     * Check if cache is ready
     */
    isCacheReady() {
        return this.thumbnailService.isCacheReady();
    }
}

// Create singleton instance
const projectEnhancementService = new ProjectEnhancementService();

export default projectEnhancementService;
